import { useState, useEffect, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { Report, ReportFilter, PageResponse } from '../types';
import { apiClient } from '../services/apiClient';

export const initialFilter: ReportFilter = {
  searchText: '',
  reportNumber: '',
  customerName: '',
  technicianName: '',
  machineName: '',
  sparePartName: '',
  showNew: true,
  showReady: true,
  showInProgress: true,
  showSigned: true,
  showClosed: true,
  showCanceled: false,
  showBillingToValidate: true,
  showBillingValidated: true,
  showBillingInvoiced: true,
  showBillingNone: true,
  commercialReference: '',
};

const FILTER_SESSION_KEY = 'proservice_report_filters';

function loadFilterFromSession(sessionKey: string, base: ReportFilter): ReportFilter {
  try {
    const stored = sessionStorage.getItem(sessionKey);
    return stored ? { ...base, ...JSON.parse(stored) } : base;
  } catch {
    return base;
  }
}

export function isFilterActive(filter: ReportFilter): boolean {
  return JSON.stringify(filter) !== JSON.stringify(initialFilter);
}

const DEFAULT_PAGE_SIZE = 20;

interface UseFilteredReportsReturn {
  reports: Report[];
  loading: boolean;
  error: Error | undefined;
  filter: ReportFilter;
  setFilter: React.Dispatch<React.SetStateAction<ReportFilter>>;
  resetFilters: () => void;
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

const DEBOUNCE_MS = 300;

const buildRequestParams = (f: ReportFilter, page: number, pageSize: number) => ({
  searchText: f.searchText || null,
  reportNumber: f.reportNumber || null,
  customerName: f.customerName || null,
  technicianName: f.technicianName || null,
  machineName: f.machineName || null,
  sparePartSearch: f.sparePartName || null,
  showNew: f.showNew,
  showReady: f.showReady,
  showInProgress: f.showInProgress,
  showSigned: f.showSigned,
  showClosed: f.showClosed,
  showCanceled: f.showCanceled,
  showBillingToValidate: f.showBillingToValidate,
  showBillingValidated: f.showBillingValidated,
  showBillingInvoiced: f.showBillingInvoiced,
  showBillingNone: f.showBillingNone,
  commercialReference: f.commercialReference || null,
  page: page - 1, // Spring Boot uses 0-indexed pages
  size: pageSize,
});

const filterFetcher = async ([, paramsJson]: readonly [string, string]): Promise<PageResponse<Report>> => {
  return await apiClient.filterReports(JSON.parse(paramsJson));
};

/**
 * @param filterOverrides Valori di default diversi dal generico `initialFilter` (es. home Finance:
 * solo report CLOSED). Va passato come costante stabile (definita fuori dal componente), non ricreato
 * a ogni render.
 * @param sessionKey Chiave sessionStorage separata, cosi' filtri di pagine diverse (dashboard generica
 * vs home Finance) non si sovrascrivono a vicenda.
 */
export const useFilteredReports = (
  filterOverrides?: Partial<ReportFilter>,
  sessionKey: string = FILTER_SESSION_KEY
): UseFilteredReportsReturn => {
  const effectiveInitialFilter: ReportFilter = { ...initialFilter, ...filterOverrides };
  const loadFilter = useCallback(
    () => loadFilterFromSession(sessionKey, effectiveInitialFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionKey]
  );

  const [filter, setFilterState] = useState<ReportFilter>(loadFilter);
  const [debouncedFilter, setDebouncedFilter] = useState<ReportFilter>(loadFilter);
  const [page, setPageState] = useState(1); // 1-indexed
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist filter to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(filter));
    } catch {
      // sessionStorage unavailable (e.g. private browsing quota)
    }
  }, [filter, sessionKey]);

  // Debounce filter text changes and reset page on filter change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(filter);
      setPageState(1);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filter]);

  const setFilter = useCallback<React.Dispatch<React.SetStateAction<ReportFilter>>>((value) => {
    setFilterState(value);
  }, []);

  const resetFilters = useCallback(() => {
    try {
      sessionStorage.removeItem(sessionKey);
    } catch {
      // ignore
    }
    setFilterState(effectiveInitialFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newSize: number) => {
    setPageSizeState(newSize);
    setPageState(1);
  }, []);

  const swrKey = ['filterReports', JSON.stringify(buildRequestParams(debouncedFilter, page, pageSize))] as const;

  const { data, error, isLoading } = useSWR<PageResponse<Report>, Error>(
    swrKey,
    filterFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
      keepPreviousData: true,
    }
  );

  return {
    reports: data?.content ?? [],
    loading: isLoading,
    error,
    filter,
    setFilter,
    resetFilters,
    page,
    pageSize,
    totalPages: data?.totalPages ?? 0,
    totalElements: data?.totalElements ?? 0,
    setPage,
    setPageSize,
  };
};
